<template>
    <div id="app" :class="{ 'is-mobileapp': GlobalState.enableMobileAppDesign.value }">
        <div v-if="rootStore.globalError" id="global-error" v-text="rootStore.globalError" />

        <Transition v-else name="fade" mode="out-in">
            <Layout v-if="shouldShowLogin">
                <Login />
            </Layout>
            <Loader v-else-if="shouldShowLoader" id="preloader" size="2xl"></Loader>
            <Layout :show-qab="!isPublicRoute" v-else>
                <router-view v-slot="{ Component }" :key="routerViewKey">
                    <Transition :name="routeTransition" mode="out-in">
                        <div v-if="!routeExists" id="error">The requested URL does not exist.</div>
                        <div v-else-if="!isAuthorized" id="error">You don't have access to the requested URL.</div>
                        <component :is="Component" v-else />
                    </Transition>
                </router-view>
            </Layout>
        </Transition>

        <OverlayContainer />

        <div id="popover-portal"></div>

        <Transition name="fade" mode="out-in">
            <div v-if="rootStore.globalLoader" id="root-loader">
                <div class="bg-wrap">
                    <Loader />
                </div>
            </div>
        </Transition>
    </div>
</template>

<script lang="ts" setup>
import '@fontsource/inter/latin-200.css';
import '@fontsource/inter/latin-300.css';
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/inter/latin-800.css';
import '@fontsource/inter/latin-900.css';

import './shared/styles/base.css';

import { OverlayContainer } from '@signal24/vue-foundation';
import { computed, onMounted } from 'vue';

import Layout from '@/shared/components/layout.vue';
import Loader from '@/shared/components/loader.vue';

import { GlobalState } from './global';
import Login from './modules/core/login.vue';
import { useAutofillPrevention } from './shared/helpers/autofill.helpers';
import { usePageTitleHelpers } from './shared/helpers/page-title.helpers';
import { Freshdesk } from './shared/services/freshdesk';
import { useStore } from './store';

import { useAppAuth } from './app.auth';
import { useAppMobileHelpers } from './app.mobile';
import { useAppRouter } from './app.router';
import './app.sw';
import { useAppServiceWorker } from './app.sw';
import { useTenantConfiguration } from './app.tenant-configuration';
import { useZSHelpers } from './app.zshelpers';

const rootStore = useStore();

const { hasLoadedPermissions, hasLoadedUserSession, isAuthorized } = useAppAuth();
const { isPublicRoute, isRouterReady, routeExists, routeTransition, routerViewKey } = useAppRouter();
useAppServiceWorker();
useTenantConfiguration();

const shouldShowLogin = computed(() => {
    if (!isRouterReady.value) return false;
    if (!hasLoadedUserSession.value) return false;
    if (isPublicRoute.value) return false;
    if (GlobalState.user.value) return false;
    return true;
});
const shouldShowLoader = computed(() => {
    if (!isRouterReady.value) return true;
    if (!hasLoadedUserSession.value) return true;
    if (isPublicRoute.value) return false;
    if (!hasLoadedPermissions.value) return true;
    return false;
});

usePageTitleHelpers();
useAutofillPrevention();
useAppMobileHelpers();
useZSHelpers();

onMounted(() => {
    Freshdesk.prepare();
});
</script>

<style>
@reference '#tw.css';

#app {
    @apply flex-1 flex;
}

#global-error {
    @apply flex-1 flex justify-center items-center bg-gray-800 text-2xl text-red-300;
}

#popover-portal {
    @apply absolute w-full h-full top-0 left-0 pointer-events-none flex;
}

#preloader {
    @apply flex-1 justify-self-center self-center;
}

#root-loader {
    @apply absolute top-0 left-0 w-full h-full flex items-end justify-center p-4;

    .bg-wrap {
        @apply bg-slate-100 rounded-full shadow-lg;
    }
}

#error {
    @apply items-center justify-center text-xl text-red-300;
}
</style>
